import type { IPeerConnection } from '../types';
import { VideoSenderSuspension } from '../VideoSenderSuspension';
import { nativeIceServers } from './NativeIceServerPolicy';
import { NativeVideoMessage, NativeVideoSettings, NativeVideoSource, NATIVE_VIDEO_CHANNEL, parseNativeVideoMessage } from './NativeVideoProtocol';
import { DirectStreamDriver, DirectStreamPort, NativeBootstrapBudget, selectSource } from './stream/StreamSourceSelector';
import {
  NativeVideoRecoveryLadder, WebCodecsFallbackReason, webCodecsFallbackReason, webCodecsStallRestartReady,
} from './NativeVideoRecoveryLadder';
import { nativeVideoRejectReason } from './NativeVideoReject';
import { SenderClockAnchor } from './SenderClockAnchor';
import { readWebCodecsEncodeDebug } from './WebCodecsEncodeDebug';
import { NativeRtpTelemetry, type NativeEncodeDebug } from './NativeEncodeDebug';
import {
  systemClock, type NativeVideoClock, type NativeVideoRejectPayload, type NativeVideoTransport,
  type NativeVideoViewer as Viewer, type NativeHubPacket, type VideoStreamingStatus,
} from './NativeVideoHostTypes';

export type {
  NativeHubPacket, NativeVideoRejectPayload, NativeVideoTransport, VideoRecoveryState, VideoStreamingStatus,
} from './NativeVideoHostTypes';

/**
 * One native video session per authorized primary peer; never trusts client identity/camera/ICE.
 * WebCodecs playback is exclusive: while it is selected the Electron video sender stays suspended
 * and every failure, including the guest's own fallback, restarts WebCodecs instead of reverting.
 */
export class NativeVideoHost {
  private viewers = new Map<string, Viewer>();
  private unsubscribe: () => void;
  private unsubscribeRejects: () => void = () => {};
  private nextStream = 1;
  constructor(
    private transport: NativeVideoTransport,
    private settings: NativeVideoSettings,
    private readonly clock: NativeVideoClock = systemClock,
    private readonly directMedia?: { transport: DirectStreamPort; token: Uint8Array | (() => Uint8Array) },
  ) {
    this.unsubscribe = transport.subscribe((packet) => this.reply(packet));
    if (transport.subscribeRejects) {
      this.unsubscribeRejects = transport.subscribeRejects((payload) => this.handleHubReject(payload));
    }
  }

  /** ShadowCam names required by attached viewers (for host main-process Cam00 policy). */
  camerasInUse(): string[] {
    return [...new Set([...this.viewers.values()].map((viewer) => viewer.camera))];
  }

  /** Updates cameraName for an attached viewer; the next offer / restart uses it. */
  setCamera(viewerId: string, cameraName: string): void {
    const viewer = this.viewers.get(viewerId);
    if (!viewer || viewer.camera === cameraName) return;
    viewer.camera = cameraName;
  }

  /** Call only after the normal host signaling layer has accepted the player. */
  attach(id: string, peer: IPeerConnection, camera: string, source: NativeVideoSource = 'shadowcam'): void {
    this.detach(id);
    const viewer: Viewer = {
      status: this.webCodecsOnly()
        ? { viewerId: id, active: 'native', pending: true, playback: 'webcodecs', requestedCodec: this.settings.codec, sessionRestarts: 0, recoveryState: 'idle' }
        : { viewerId: id, active: 'electron', pending: false, sessionRestarts: 0, recoveryState: 'idle' },
      peer, camera, session: '', hello: false, offered: false, closed: false, retries: 0,
      sessionRestarts: 0, recoveryState: 'idle', seenNative: false, restartQueued: false, restartToken: 0,
      chain: Promise.resolve(), video: new VideoSenderSuspension(), bootstrap: new NativeBootstrapBudget(),
      ladder: new NativeVideoRecoveryLadder(), onData: () => {},
      source, signalingLabel: NATIVE_VIDEO_CHANNEL, prefersStream: source === 'stream', streamId: this.nextStream++,
      senderClock: new SenderClockAnchor(),
    };
    viewer.onData = (...args) => {
      const event = args[0] as { source?: string; channel?: { label?: string }; data?: unknown };
      const label = event.channel?.label;
      if (event.source === 'outgoing' || (label !== NATIVE_VIDEO_CHANNEL && label !== 'data')) return;
      const m = parseNativeVideoMessage(event.data);
      if (!m || this.viewers.get(id) !== viewer) return;
      if (m.type === 'hello' && label) viewer.signalingLabel = label;
      if (m.type === 'diagnostics') {
        if (!m.diagnostics || !viewer.diagnosticsEnabled || !viewer.offered || m.session !== viewer.session ||
          m.diagnostics.sequence <= (viewer.diagnosticSequence ?? 0) ||
          this.clock.now() - (viewer.diagnosticTime ?? -Infinity) < 900) return;
        viewer.diagnosticTime = this.clock.now();
        viewer.diagnosticSequence = m.diagnostics.sequence;
        try { this.hub(viewer, 'diagnostics', { diagnostics: m.diagnostics }); } catch { /* Diagnostics cannot interrupt playback. */ }
        return;
      }
      this.enqueue(viewer, () => this.message(viewer, m));
    };
    this.viewers.set(id, viewer);
    peer.on('channelData', viewer.onData);
    if (this.webCodecsOnly()) this.enqueue(viewer, () => this.suspendElectron(viewer));
  }

  /** Changes only video; the primary connection and permissions remain intact. */
  configure(settings: NativeVideoSettings): void {
    this.settings = settings;
    for (const viewer of this.viewers.values()) {
      this.enqueue(viewer, async () => {
        if (this.webCodecsOnly()) { await this.stayOnWebCodecs(viewer, 'settings-changed'); return; }
        const terminal = viewer.playback === 'webcodecs' ? 'settings-changed' as const : undefined;
        await this.fallback(viewer, terminal ?? 'Video backend changed', terminal);
        viewer.retries = 0;
        viewer.sessionRestarts = 0;
        viewer.seenNative = false;
        viewer.ladder = new NativeVideoRecoveryLadder();
        if (this.settings.backend === 'native') {
          viewer.recoveryState = 'idle';
          await this.begin(viewer);
        }
      });
    }
  }

  /** Actual handoff state, distinct from the persisted preference. */
  getStatus(): VideoStreamingStatus[] {
    return [...this.viewers.values()].map((viewer) => ({ ...viewer.status }));
  }

  /**
   * Host wall time at which the native sender stamped `sentUs` on a WebCodecs frame, for the
   * calibrated latency probe; undefined until the sender has reported its clock anchor.
   */
  senderWallMs(viewerId: string, sentUs: number): number | undefined {
    return this.viewers.get(viewerId)?.senderClock.wallMsFor(sentUs);
  }

  /** Latest native sender measurement, or undefined once the report is a few seconds old. */
  encodeDebug(viewerId: string): NativeEncodeDebug | undefined {
    const viewer = this.viewers.get(viewerId);
    if (!viewer?.encodeDebug || viewer.encodeDebugAtMs === undefined) return undefined;
    if (this.clock.now() - viewer.encodeDebugAtMs > 3_000) return undefined;
    return viewer.encodeDebug;
  }

  private handleHubReject(payload: NativeVideoRejectPayload): void {
    const reason = nativeVideoRejectReason(typeof payload.reason === 'string' ? payload.reason : undefined) ?? payload.reason;
    // A reject naming a session that is no longer ours (retired before its offer reached IPC)
    // must not be charged to whichever session is pending now; only an anonymous reject may.
    const viewer = payload.sessionId
      ? [...this.viewers.values()].find((value) => value.session === payload.sessionId)
      : [...this.viewers.values()].find((value) => value.status.pending);
    if (!viewer || typeof reason !== 'string') return;
    this.enqueue(viewer, async () => {
      if (viewer.playback === 'webcodecs') { this.queueRestart(viewer, reason); return; }
      this.mark(viewer, 'electron', false, { reason });
    });
  }

  private webCodecsOnly(): boolean {
    return this.settings.backend === 'native' && this.settings.playback === 'webcodecs';
  }
  /** Best effort before negotiation: an unnegotiated sender is suspended again when the session begins. */
  private async suspendElectron(v: Viewer): Promise<void> {
    try { await v.video.suspend(v.peer.getRtcPeerConnection()); } catch { /* retried by begin() */ }
  }

  private async begin(v: Viewer, keepNative = false): Promise<void> {
    if (v.closed || this.settings.backend !== 'native' || !v.hello || v.session) return;
    v.playback = this.settings.playback ?? 'rtp';
    v.codec = this.settings.codec;
    v.source = selectSource(this.settings, { prefersStream: v.prefersStream });
    if (v.playback === 'webcodecs') {
      await v.video.suspend(v.peer.getRtcPeerConnection());
      if (v.closed || v.session || !this.webCodecsOnly()) return;
      const blocked = !v.codecs?.length ? 'guest-unsupported' as const : !v.codecs.includes(v.codec) ? 'codec-unsupported' as const : undefined;
      if (blocked) {
        // Nothing to retry until the guest greets again with a usable decoder (e.g. once its surface attaches).
        v.recoveryState = 'idle'; v.fallbackReason = blocked;
        this.mark(v, 'native', false, { playback: 'webcodecs', reason: blocked }); return;
      }
    }
    v.configuredEpoch = undefined; v.pendingReady = undefined; v.fallbackReason = undefined;
    v.diagnosticTime = undefined; v.diagnosticSequence = 0; v.diagnosticsEnabled = false;
    v.session = `native-player-${crypto.randomUUID()}`;
    v.recoveryState = keepNative ? 'restart' : 'idle';
    if (v.playback === 'webcodecs') this.mark(v, 'native', true, { playback: v.playback, codec: keepNative ? v.codec : undefined, reason: v.status.reason });
    else this.mark(v, keepNative ? 'native' : 'electron', true, keepNative ? { playback: v.playback, codec: v.codec } : {});
    this.send(v, { type: 'configure', codec: v.codec, playback: v.playback, ...(v.source === 'stream' ? { source: 'stream' as const } : {}) });
    if (v.source === 'stream') this.openStream(v);
    const session = v.session;
    this.clock.clearTimeout(v.timer);
    v.timer = this.clock.setTimeout(() => {
      this.enqueue(v, async () => {
        if (v.session !== session) return;
        if (v.playback === 'webcodecs') this.queueRestart(v, 'WebCodecs startup timed out');
        else await this.recover(v, 'Native video startup timed out');
      });
    }, 20000);
  }

  private async message(v: Viewer, m: NativeVideoMessage): Promise<void> {
    if (v.closed) return;
    if (m.type === 'hello') { v.hello = true; v.codecs = m.webCodecs?.codecs; await this.begin(v); return; }
    if (!v.session || m.session !== v.session) return;
    if (m.type === 'offer' && !v.offered) {
      v.offered = true;
      if (v.source === 'stream') { await this.answerStream(v, m.sdp ?? ''); return; }
      const config = v.peer.getRtcPeerConnection()?.getConfiguration() ?? {};
      this.hub(v, 'sdpOffer', {
        codec: v.codec, playback: v.playback, cameraName: v.camera, sdp: { type: 'offer', sdp: m.sdp },
        iceServers: nativeIceServers(config), forceRelay: config.iceTransportPolicy === 'relay',
        ...(m.videoMode ? { videoMode: m.videoMode } : {}),
      });
    } else if (m.type === 'ice' && v.offered) {
      if (v.source === 'stream' && m.candidate?.candidate) { v.direct?.addIce(m.candidate.candidate); return; }
      this.hub(v, 'iceCandidate', { candidates: { json: [JSON.stringify(m.candidate)] } });
    } else if (m.type === 'ready' && v.offered) {
      await this.ready(v, m);
    } else if (m.type === 'fallback' || m.type === 'close') {
      if (v.playback === 'webcodecs') this.queueRestart(v, m.type === 'fallback' ? m.reason : 'Player closed native video');
      else await this.fallback(v, m.reason || 'Player requested Electron video');
    }
  }

  private async ready(v: Viewer, m: NativeVideoMessage): Promise<void> {
    if (v.playback === 'webcodecs') {
      if (m.playback !== 'webcodecs' || m.codec !== v.codec || !m.configEpoch || !m.firstFrameId) return;
      if (!v.configuredEpoch || m.configEpoch > v.configuredEpoch) { v.pendingReady = m; return; }
      if (m.configEpoch !== v.configuredEpoch) return;
    } else if (m.playback === 'webcodecs') return;
    const session = v.session;
    await v.bootstrap.restore();
    await v.video.suspend(v.peer.getRtcPeerConnection());
    if (v.closed || v.session !== session) { if (!this.webCodecsOnly()) await v.video.resume(); return; }
    if (v.playback === 'webcodecs') this.hub(v, 'videoReady', { configEpoch: m.configEpoch });
    this.clock.clearTimeout(v.timer); v.timer = undefined; v.pendingReady = undefined;
    v.seenNative = true; v.recoveryState = 'idle'; v.fallbackReason = undefined;
    v.stallSinceMs = undefined; v.keyframeAtMs = undefined;
    this.clock.clearTimeout(v.phaseTimer); this.clock.clearTimeout(v.damageTimer);
    v.phaseTimer = v.damageTimer = undefined;
    if (v.restartQueued) { ++v.restartToken; this.clock.clearTimeout(v.retryTimer); v.retryTimer = undefined; v.restartQueued = false; }
    this.armHealthy(v);
    this.mark(v, 'native', false, { playback: v.playback, codec: m.codec });
  }

  private openStream(v: Viewer): void {
    const media = this.directMedia;
    if (!media || !v.codec) { void this.enqueue(v, () => this.giveUp(v, 'gpu-path-unavailable')); return; }
    v.direct?.close();
    v.direct = new DirectStreamDriver(media.transport, this.clock,
      (message) => this.send(v, message),
      (stats) => this.hub(v, 'diagnostics', { streamStats: stats }),
      () => { void this.enqueue(v, () => this.giveUp(v, 'gpu-path-unavailable')); });
    const token = typeof media.token === 'function' ? media.token() : media.token;
    v.direct.open({ streamId: v.streamId, codec: v.codec, playback: v.playback ?? 'rtp', token, camera: v.camera });
  }
  private async answerStream(v: Viewer, sdp: string): Promise<void> {
    const config = v.peer.getRtcPeerConnection()?.getConfiguration() ?? {};
    await v.direct?.accept(sdp, config.iceServers ?? []);
  }
  private hub(v: Viewer, action: string, message: Record<string, unknown> = {}): void {
    this.transport.send({
      header: { target: 'peer', action, source: 'host', isReply: false },
      body: { message: { ...message, fromId: v.session, toId: 'playjector-native' } },
    });
  }
  private send(v: Viewer, m: Partial<NativeVideoMessage> & Pick<NativeVideoMessage, 'type'>): void {
    v.peer.send(JSON.stringify({ ...m, nativeVideo: 1, session: v.session }), v.signalingLabel);
  }
  private reply(packet: NativeHubPacket): void {
    if (packet.header?.target !== 'peer' || !packet.header.isReply) return;
    const body = packet.body?.message;
    const v = [...this.viewers.values()].find((value) => value.session && value.session === body?.['toId']);
    if (!v) return;
    const session = v.session;
    const recover = (reason: string) => this.enqueue(v, async () => {
      if (v.session === session) await this.recover(v, reason);
    });
    const action = packet.header.action;
    if (action === 'state' && body['reason'] === 'webcodecs-telemetry') {
      if (v.playback === 'webcodecs') {
        v.senderClock.observe(body);
        const read = readWebCodecsEncodeDebug(body, this.clock.now(), v.encodeDebugAtMs);
        if (read) { v.encodeDebug = read.debug; v.encodeDebugAtMs = read.atMs; }
      }
    } else if (action === 'state' && body['reason'] === 'rtp-telemetry' && v.playback === 'rtp') {
      const read = (v.rtpTelemetry ??= new NativeRtpTelemetry()).read(body);
      if (read) { v.encodeDebug = read; v.encodeDebugAtMs = this.clock.now(); }
    } else if (action === 'state' && body['reason'] === 'webcodecs-configured') {
      this.enqueue(v, async () => {
        const epoch = body['configEpoch'];
        if (v.session !== session || v.playback !== 'webcodecs' || body['playback'] !== 'webcodecs' ||
          body['codec'] !== v.codec || !Number.isInteger(epoch) || epoch < 1 || epoch > 0xffffffff ||
          epoch <= (v.configuredEpoch ?? 0)) return;
        v.configuredEpoch = epoch;
        const ready = v.pendingReady; v.pendingReady = undefined;
        if (ready) await this.ready(v, ready);
      });
    } else if (action === 'sdpAnswer') {
      v.diagnosticsEnabled = body['diagnosticsEnabled'] === true;
      this.send(v, { type: 'answer', sdp: body['sdp']?.sdp, diagnosticsEnabled: v.diagnosticsEnabled });
    }
    else if (action === 'iceCandidate') {
      try {
        for (const raw of body['candidates'].json)
          this.send(v, { type: 'ice', candidate: typeof raw === 'string' ? JSON.parse(raw) : raw });
      } catch { recover('Invalid native ICE reply'); }
    } else if (action === 'state' && body['reason'] === 'disconnected') {
      if (v.playback === 'webcodecs') { this.noteDamage(v); return; }
      if (v.disconnectTimer) return;
      const disconnected = v.session;
      v.disconnectTimer = this.clock.setTimeout(() => this.enqueue(v, async () => {
        if (v.session === disconnected) await this.recover(v, 'Native sender disconnected');
      }), 5000);
    } else if (action === 'state' && body['reason'] === 'connected') {
      this.clock.clearTimeout(v.disconnectTimer); v.disconnectTimer = undefined;
      if (v.playback === 'webcodecs') this.cancelDamage(v, true);
    } else if (action === 'error' || action === 'fallback' || (action === 'state' && body['reason'] === 'failed')) {
      const raw = typeof body['reason'] === 'string' ? body['reason'] : 'Native sender unavailable';
      const reject = nativeVideoRejectReason(raw);
      if (v.playback === 'webcodecs') {
        this.enqueue(v, async () => { if (v.session === session) this.queueRestart(v, reject ?? raw); });
        return;
      }
      if (reject) { this.enqueue(v, async () => this.mark(v, 'electron', false, { reason: reject })); return; }
      recover(raw);
    }
  }
  private enqueue(v: Viewer, action: () => Promise<void>): Promise<void> {
    v.chain = v.chain.then(action).catch((error: unknown) => {
      if (v.playback === 'webcodecs' && !v.closed) { this.queueRestart(v, error instanceof Error ? error.message : 'Native video failed'); return; }
      return this.fallback(v, 'Native video failed');
    }).catch(() => {});
    return v.chain;
  }
  private mark(v: Viewer, active: VideoStreamingStatus['active'], pending: boolean, extra: Partial<VideoStreamingStatus> = {}): void {
    const playback = extra.playback ?? (active === 'native' ? v.playback : undefined);
    const codec = extra.codec ?? (active === 'native' ? v.status.codec : undefined);
    v.status = {
      viewerId: v.status.viewerId, active, pending, sessionRestarts: v.sessionRestarts, recoveryState: v.recoveryState,
      requestedCodec: this.settings.codec,
      ...(playback ? { playback } : {}), ...(codec ? { codec } : {}),
      ...(extra.reason ? { reason: extra.reason } : {}),
      ...(v.fallbackReason ? { fallbackReason: v.fallbackReason } : {}),
    };
  }
  private async retireNativeSession(v: Viewer, notifyPlayer: boolean, reason: string): Promise<void> {
    this.clearTimers(v);
    const restoreElectron = notifyPlayer && (v.closed || !this.webCodecsOnly());
    try {
      await v.bootstrap.restore();
      if (restoreElectron) await v.video.resume();
      if (v.session) {
        try { this.hub(v, 'close', { reason }); }
        finally { if (notifyPlayer) this.send(v, { type: 'fallback', reason }); }
      }
    } finally {
      v.direct?.close(); v.direct = undefined;
      v.session = ''; v.offered = false; v.configuredEpoch = undefined; v.pendingReady = undefined;
      v.senderClock.clear(); v.encodeDebug = undefined; v.encodeDebugAtMs = undefined; v.rtpTelemetry = undefined;
      if (restoreElectron) await v.video.resume();
    }
  }
  /**
   * WebCodecs stays selected. The player is not told to present Electron video, and the
   * Electron sender stays suspended. `restart` opens the next native session when the guest
   * has already said hello.
   */
  private async stayOnWebCodecs(v: Viewer, reason: string, restart = true): Promise<void> {
    v.retries = 0; v.sessionRestarts = 0; v.seenNative = false;
    v.ladder = new NativeVideoRecoveryLadder();
    v.recoveryState = 'idle'; v.fallbackReason = undefined; v.restartQueued = false;
    ++v.restartToken;
    await this.retireNativeSession(v, false, reason);
    if (v.closed || !this.webCodecsOnly()) return;
    await this.suspendElectron(v);
    if (restart && v.hello) await this.begin(v);
    else this.mark(v, 'native', false, { playback: 'webcodecs', reason });
  }
  /** A failure: WebCodecs retries in place while it is selected; other paths revert to Electron. */
  private async giveUp(v: Viewer, reason: string): Promise<void> {
    if (v.playback === 'webcodecs' && this.webCodecsOnly()) this.queueRestart(v, reason);
    else await this.fallback(v, reason);
  }
  private async fallback(v: Viewer, reason: string, terminal?: WebCodecsFallbackReason): Promise<void> {
    reason = reason.slice(0, 512);
    v.restartQueued = false; v.stallSinceMs = undefined; v.keyframeAtMs = undefined;
    v.fallbackReason = terminal; v.recoveryState = 'idle';
    await this.retireNativeSession(v, true, reason);
    v.status = {
      viewerId: v.status.viewerId, active: 'electron', pending: false, requestedCodec: this.settings.codec, reason,
      sessionRestarts: v.sessionRestarts, recoveryState: 'idle', ...(terminal ? { fallbackReason: terminal } : {}),
    };
  }
  private async recover(v: Viewer, reason: string): Promise<void> {
    if (v.playback === 'webcodecs') {
      if (webCodecsFallbackReason(reason)) this.queueRestart(v, reason); else this.noteDamage(v);
      return;
    }
    await this.fallback(v, reason);
    if (v.closed || this.settings.backend !== 'native' || v.retries >= 2) return;
    const delayMs = ++v.retries === 1 ? 1000 : 3000;
    v.retryTimer = this.clock.setTimeout(() => this.enqueue(v, async () => {
      v.retryTimer = undefined;
      if (this.viewers.get(v.status.viewerId) === v && !v.closed) await this.begin(v);
    }), delayMs);
  }
  private noteDamage(v: Viewer): void {
    if (v.closed || v.playback !== 'webcodecs' || v.restartQueued || v.stallSinceMs !== undefined) return;
    const start = this.clock.now();
    v.stallSinceMs = start; v.keyframeAtMs = start; v.recoveryState = 'keyframe'; v.fallbackReason = undefined;
    v.status = { ...v.status, recoveryState: 'keyframe', sessionRestarts: v.sessionRestarts };
    delete v.status.fallbackReason;
    this.clock.clearTimeout(v.healthyTimer); v.healthyTimer = undefined;
    v.phaseTimer = this.clock.setTimeout(() => this.enqueue(v, async () => {
      if (v.stallSinceMs === undefined || v.recoveryState !== 'keyframe') return;
      v.recoveryState = 'reset';
      v.status = { ...v.status, recoveryState: 'reset', sessionRestarts: v.sessionRestarts };
    }), 1000);
    v.damageTimer = this.clock.setTimeout(() => this.enqueue(v, async () => {
      if (v.stallSinceMs === undefined || v.keyframeAtMs === undefined) return;
      const now = Math.max(this.clock.now(), start + 3000);
      if (webCodecsStallRestartReady(now, v.stallSinceMs, v.keyframeAtMs, 0)) this.queueRestart(v);
    }), 3000);
  }
  private cancelDamage(v: Viewer, resume: boolean): void {
    this.clock.clearTimeout(v.phaseTimer); this.clock.clearTimeout(v.damageTimer);
    v.phaseTimer = v.damageTimer = undefined; v.stallSinceMs = undefined; v.keyframeAtMs = undefined;
    if (v.restartQueued) { ++v.restartToken; this.clock.clearTimeout(v.retryTimer); v.retryTimer = undefined; v.restartQueued = false; }
    if (resume && v.seenNative && v.session && v.recoveryState !== 'idle') {
      v.recoveryState = 'idle'; v.fallbackReason = undefined;
      v.status = { ...v.status, recoveryState: 'idle', sessionRestarts: v.sessionRestarts };
      delete v.status.fallbackReason;
      this.armHealthy(v);
    }
  }
  /** `reason` stays on the status (and the video-path log) until a session reaches ready. */
  private queueRestart(v: Viewer, reason?: string): void {
    if (v.closed || v.playback !== 'webcodecs' || this.settings.backend !== 'native' || v.restartQueued) return;
    v.restartQueued = true;
    const token = ++v.restartToken;
    this.clock.clearTimeout(v.phaseTimer); this.clock.clearTimeout(v.damageTimer); this.clock.clearTimeout(v.healthyTimer);
    v.phaseTimer = v.damageTimer = v.healthyTimer = undefined; v.stallSinceMs = undefined; v.keyframeAtMs = undefined;
    const delay = v.ladder.nextDelayMs(this.clock.now()) ?? 2000;
    v.recoveryState = 'restart'; v.fallbackReason = undefined;
    v.status = { ...v.status, recoveryState: 'restart', sessionRestarts: v.sessionRestarts, ...(reason ? { reason: reason.slice(0, 512) } : {}) };
    delete v.status.fallbackReason;
    v.retryTimer = this.clock.setTimeout(() => {
      v.retryTimer = undefined;
      if (v.restartToken !== token) return;
      this.enqueue(v, () => this.finishRestart(v, token));
    }, delay);
  }
  private async finishRestart(v: Viewer, token: number): Promise<void> {
    try {
      if (token !== v.restartToken || v.closed || this.viewers.get(v.status.viewerId) !== v || this.settings.backend !== 'native') return;
      const keepNative = v.seenNative;
      await this.retireNativeSession(v, false, 'webcodecs-restart');
      if (token !== v.restartToken || v.closed || this.viewers.get(v.status.viewerId) !== v || this.settings.backend !== 'native') return;
      v.sessionRestarts += 1; v.recoveryState = 'restart'; v.fallbackReason = undefined;
      await this.begin(v, keepNative);
    } finally { if (token === v.restartToken) v.restartQueued = false; }
  }
  private armHealthy(v: Viewer): void {
    this.clock.clearTimeout(v.healthyTimer);
    const start = this.clock.now();
    v.ladder.recordHealthy(start);
    v.healthyTimer = this.clock.setTimeout(() => v.ladder.recordHealthy(start + 10000), 10000);
  }
  private clearTimers(v: Viewer): void {
    for (const timer of [v.timer, v.disconnectTimer, v.retryTimer, v.phaseTimer, v.damageTimer, v.healthyTimer]) this.clock.clearTimeout(timer);
    v.timer = v.disconnectTimer = v.retryTimer = v.phaseTimer = v.damageTimer = v.healthyTimer = undefined;
  }

  /** Tear down the native subscription on kick, reconnect, stream stop or primary-peer loss. */
  detach(id: string): void {
    const v = this.viewers.get(id);
    if (!v) return;
    this.viewers.delete(id);
    v.peer.off('channelData', v.onData);
    v.closed = true; ++v.restartToken;
    this.clearTimers(v);
    this.enqueue(v, () => this.fallback(v, 'Player disconnected'));
  }
  async resetVideo(replacement?: MediaStreamTrack | null): Promise<void> {
    await Promise.all([...this.viewers.values()].map((v) => this.enqueue(v, async () => {
      if (this.webCodecsOnly()) { await this.stayOnWebCodecs(v, 'Camera changed', !!replacement); return; }
      await this.fallback(v, 'Camera changed');
      v.retries = 0; v.sessionRestarts = 0; v.seenNative = false; v.ladder = new NativeVideoRecoveryLadder();
      v.recoveryState = 'idle'; v.fallbackReason = undefined;
      if (replacement) await this.begin(v);
    })));
  }
  destroy(): void {
    for (const id of this.viewers.keys()) this.detach(id);
    this.unsubscribe();
    this.unsubscribeRejects();
  }
}
